import {
  GlobalOutlined,
  InstagramOutlined,
  MailOutlined,
  TelegramFilled,
  WhatsAppOutlined,
  YoutubeFilled,
} from "@ant-design/icons";
import type { PortalSocialType } from "@axl/membership-sdk";
import cn from "classnames";
import type { ReactNode } from "react";

import { usePortalDesign } from "@app/shared/lib/design/portal-design";
import { useLoc, useSchool } from "@app/shared/lib/hooks";

import styles from "./PortalFooter.module.scss";

const VkIcon = () => (
  <span role="img" aria-hidden className="anticon">
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
      <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.576-1.496c.588-.19 1.341 1.26 2.14 1.818.605.422 1.064.33 1.064.33l2.137-.03s1.117-.071.587-.964c-.043-.073-.308-.661-1.588-1.87-1.34-1.264-1.16-1.059.453-3.246.983-1.332 1.376-2.145 1.253-2.493-.117-.332-.84-.244-.84-.244l-2.406.015s-.178-.025-.31.056c-.13.079-.212.262-.212.262s-.382 1.03-.89 1.907c-1.07 1.85-1.499 1.948-1.674 1.832-.407-.267-.305-1.075-.305-1.648 0-1.793.267-2.54-.521-2.733-.262-.065-.454-.107-1.123-.114-.858-.009-1.585.003-1.996.208-.274.136-.485.44-.356.457.159.022.519.099.71.363.246.341.237 1.107.237 1.107s.142 2.11-.33 2.371c-.325.18-.77-.187-1.725-1.865-.489-.859-.859-1.81-.859-1.81s-.07-.176-.198-.272c-.154-.115-.37-.151-.37-.151l-2.286.015s-.343.01-.469.161c-.112.134-.009.411-.009.411s1.79 4.258 3.817 6.403c1.858 1.967 3.968 1.838 3.968 1.838h.957z" />
    </svg>
  </span>
);

const SOCIAL_ICONS: Record<PortalSocialType, ReactNode> = {
  telegram: <TelegramFilled />,
  vk: <VkIcon />,
  youtube: <YoutubeFilled />,
  instagram: <InstagramOutlined />,
  whatsapp: <WhatsAppOutlined />,
  website: <GlobalOutlined />,
  email: <MailOutlined />,
};

/** Brand names are not translated; the two generic icons are. */
const BRAND_NAMES: Partial<Record<PortalSocialType, string>> = {
  telegram: "Telegram",
  vk: "VK",
  youtube: "YouTube",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
};

type Props = {
  /** `page` = signed-in pages outside the lesson (setting «footer on pages»), `learn` = the lesson player. */
  place: "page" | "learn";
};

/**
 * Portal design `footer.mode = 'structured'`: link columns, social icons and a small-print line, built from
 * plain text and http(s)/mailto/tel links the SDK has already filtered. Shown only where the school's existing
 * footer switches allow it (`settings.isPageFooterEnabled` / `isLearnPageFooterEnabled`).
 * Hook classes for school CSS: `school-footer`, `-columns`, `-column`, `-socials`, `-legal`.
 */
export const PortalFooter: React.FC<Props> = ({ place }) => {
  const { footer } = usePortalDesign();
  const school = useSchool();
  const loc = useLoc();
  const settings = school.settings as { isPageFooterEnabled?: boolean; isLearnPageFooterEnabled?: boolean } | undefined;
  const enabled = place === "page" ? settings?.isPageFooterEnabled : settings?.isLearnPageFooterEnabled;

  if (footer.mode !== "structured" || !enabled) return null;
  if (!footer.columns.length && !footer.socials.length && !footer.legal) return null;

  const external = (url: string) => (/^https?:/i.test(url) ? { target: "_blank", rel: "noopener noreferrer" } : {});
  const socialName = (type: PortalSocialType) =>
    BRAND_NAMES[type] ??
    (type === "email" ? loc.word("Email") : loc.word("School.footer.website", { default: "Website" }));

  return (
    <footer className={cn(styles.footer, "school-footer")} data-place={place}>
      {footer.columns.length > 0 && (
        <div className={cn(styles.columns, "school-footer-columns")}>
          {footer.columns.map((col, i) => (
            <div key={i} className={cn(styles.column, "school-footer-column")}>
              {col.title && <div className={styles.columnTitle}>{col.title}</div>}
              {col.links.map((l, j) => (
                <a key={j} className={styles.link} href={l.url} {...external(l.url)}>
                  {l.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
      {(footer.socials.length > 0 || footer.legal) && (
        <div className={styles.bottom}>
          {footer.legal && <div className={cn(styles.legal, "school-footer-legal")}>{footer.legal}</div>}
          {footer.socials.length > 0 && (
            <div className={cn(styles.socials, "school-footer-socials")}>
              {footer.socials.map((s, i) => (
                <a
                  key={i}
                  className={styles.social}
                  href={s.url}
                  aria-label={socialName(s.type)}
                  title={socialName(s.type)}
                  data-type={s.type}
                  {...external(s.url)}
                >
                  {SOCIAL_ICONS[s.type]}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </footer>
  );
};
